import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { SearchBox } from "../../../../components/Search";
import "../Mod/ModTable/ModIndex.css";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  width = "620px",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
      const t = setTimeout(() => setIsMounted(false), 160);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isMounted ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isMounted, onClose]);

  if (!isMounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ease-in-out ${
          isAnimating ? "opacity-50" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        ref={modalRef}
        className={`relative bg-white shadow-xl flex flex-col overflow-hidden transition-all duration-200 ease-in-out ${
          isAnimating
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-2 scale-95"
        }`}
        style={{
          width,
          maxWidth: "100vw",
          maxHeight: "90vh",
          borderRadius: "12px",
          border: "3px solid #F3F5F8",
          zIndex: 10000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

// Bulk apply for BidCreation rate fields.
// onLocalApply: Array<{ slotIndex, classKey, field, value }>

interface BidCreationFilterProps {
  isOpen: boolean;
  onClose: () => void;
  uniqueBlocks?: number[];
  classKeys?: string[];
  loading?: boolean;
  classVcRanges?: Record<string, { min: number; max: number }>;
  onLocalApply?: (
    changes: Array<{
      slotIndex: number;
      classKey: string;
      field: string;
      value: number;
    }>,
  ) => void;
}

const RATE_FIELDS: { field: string; label: string }[] = [
  { field: "classRate", label: "Class Rate" },
  { field: "singleBuyPrice", label: "Single Bid (Buy) Rate" },
  { field: "blockNetPrice", label: "Block Bid Rate" },
  { field: "singleSellPrice", label: "Single Bid (Sell) Rate" },
];

const RATE_FIELDS_VALIDATED = new Set(["classRate"]);

const BidCreationFilter: React.FC<BidCreationFilterProps> = ({
  isOpen,
  onClose,
  uniqueBlocks = [],
  classKeys = [],
  loading = false,
  classVcRanges = {},
  onLocalApply,
}) => {
  const [selectedBlocks, setSelectedBlocks] = useState<any[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<any[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [batchError, setBatchError] = useState<string | null>(null);

  const blockOptions = useMemo(
    () => uniqueBlocks.map((b) => ({ value: String(b), label: String(b) })),
    [uniqueBlocks],
  );

  const classOptions = useMemo(
    () => classKeys.map((k) => ({ value: k, label: `Class ${k}` })),
    [classKeys],
  );

  const resetForm = useCallback(() => {
    setSelectedBlocks([]);
    setSelectedClasses([]);
    setFieldValues({});
    setBatchError(null);
  }, []);

  const hasAnyValue = Object.values(fieldValues).some((v) => v !== "");

  const selectedBlockNums = useMemo(
    () => selectedBlocks.map((b: any) => Number(b.value ?? b)),
    [selectedBlocks],
  );
  const selectedClassKeys = useMemo(
    () => selectedClasses.map((c: any) => String(c.value ?? c)),
    [selectedClasses],
  );

  const buildChanges = useCallback(
    (blocks: number[], classes: string[]) => {
      const changes: Array<{
        slotIndex: number;
        classKey: string;
        field: string;
        value: number;
      }> = [];

      const targetBlocks = blocks.length > 0 ? blocks : uniqueBlocks;
      const targetClasses = classes.length > 0 ? classes : classKeys;

      for (const slot of targetBlocks) {
        for (const ck of targetClasses) {
          for (const { field } of RATE_FIELDS) {
            const raw = fieldValues[field];
            if (raw === undefined || raw === "") continue;
            const num = Number(raw);
            if (!Number.isFinite(num)) continue;
            changes.push({ slotIndex: slot, classKey: ck, field, value: num });
          }
        }
      }
      return changes;
    },
    [fieldValues, uniqueBlocks, classKeys],
  );

  const handleApply = () => {
    if (!hasAnyValue) return;
    const changes = buildChanges(selectedBlockNums, selectedClassKeys);
    if (!changes.length) return;

    // VC range validation: check each rate field value against per-class range
    const targetClasses =
      selectedClassKeys.length > 0 ? selectedClassKeys : classKeys;
    const violations: string[] = [];
    for (const ck of targetClasses) {
      const range = classVcRanges[ck];
      if (!range) continue;
      for (const { field } of RATE_FIELDS) {
        if (!RATE_FIELDS_VALIDATED.has(field)) continue;
        const raw = fieldValues[field];
        if (!raw) continue;
        const num = Number(raw);
        if (!Number.isFinite(num)) continue;
        if (num < range.min || num > range.max) {
          violations.push(
            `Class ${ck} – ${RATE_FIELDS.find((f) => f.field === field)?.label}: ${num.toFixed(2)} outside [${range.min.toFixed(2)} – ${range.max.toFixed(2)}]`,
          );
        }
      }
    }

    if (violations.length) {
      setBatchError(
        `${violations.length} value(s) outside VC range:\n• ${violations.slice(0, 5).join("\n• ")}${violations.length > 5 ? `\n…and ${violations.length - 5} more` : ""}`,
      );
      return;
    }

    setBatchError(null);
    onLocalApply?.(changes);
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="z-[100000]">
      <Modal isOpen={isOpen} onClose={handleClose} width="800px">
        {/* Header */}
        <div
          className="p-3"
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #DFEAF2",
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-md font-medium" style={{ color: "#525062" }}>
              Batch Customize Bids
            </h2>
            <button
              onClick={handleClose}
              className="hover:bg-gray-100 p-1 rounded-full transition-colors"
              style={{ color: "#6C757D" }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex mod-table-load-in" style={{ minHeight: "360px" }}>
          {/* Filters panel */}
          <div className="flex bg-[#F3F5F8] p-2" style={{ minHeight: "360px" }}>
            <div
              className="p-4 space-y-3"
              style={{
                width: "240px",
                backgroundColor: "#FFFFFFA3",
                borderRadius: "15px",
              }}
            >
              <h2
                className="text-base font-medium mb-4"
                style={{ color: "#6C757D" }}
              >
                Filters
                <hr className="mt-1 border-[#DFEAF2]" />
              </h2>

              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Time Block
                </label>
                <SearchBox
                  options={blockOptions}
                  isMany
                  placeholder="All blocks"
                  selectedOptions={selectedBlocks}
                  onChange={setSelectedBlocks}
                  menuPortalTarget={document.body}
                />
              </div>

              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Class
                </label>
                <SearchBox
                  options={classOptions}
                  isMany
                  placeholder="All classes"
                  selectedOptions={selectedClasses}
                  onChange={setSelectedClasses}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>

          {/* Fields panel */}
          <div className="space-y-4 bg-[#F3F5F8] p-2 flex-1">
            <div
              className="p-4 space-y-3"
              style={{
                height: "100%",
                backgroundColor: "#FFFFFFA3",
                borderRadius: "15px",
              }}
            >
              <h2
                className="text-base font-medium mb-4"
                style={{ color: "#6C757D" }}
              >
                Customize Rates
                <hr className="mt-1 border-[#DFEAF2]" />
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {RATE_FIELDS.map(({ field, label }) => {
                  // Collect VC ranges for selected (or all) classes
                  const targetClasses =
                    selectedClassKeys.length > 0 ? selectedClassKeys : classKeys;
                  const ranges = targetClasses
                    .map((ck) => classVcRanges[ck])
                    .filter(Boolean) as { min: number; max: number }[];
                  const overallMin =
                    ranges.length > 0
                      ? Math.min(...ranges.map((r) => r.min))
                      : null;
                  const overallMax =
                    ranges.length > 0
                      ? Math.max(...ranges.map((r) => r.max))
                      : null;
                  const hasRange =
                    RATE_FIELDS_VALIDATED.has(field) &&
                    overallMin !== null &&
                    overallMax !== null;
                  const raw = fieldValues[field] ?? "";
                  const num = Number(raw);
                  const isViolation =
                    hasRange &&
                    raw !== "" &&
                    Number.isFinite(num) &&
                    (num < overallMin! || num > overallMax!);

                  return (
                    <div key={field}>
                      <label className="block text-xs font-medium text-[#6C757D] mb-1">
                        <span className="block">{label}</span>
                        {hasRange && (
                          <span className="block text-[10px] font-normal text-[#7aabb2]">
                            VC: {overallMin!.toFixed(2)}–{overallMax!.toFixed(2)}
                          </span>
                        )}
                      </label>
                      <input
                        step={field === "classRate" ? "0.001" : "0.01"}
                        inputMode="decimal"
                        type="number"
                        value={raw}
                        onChange={(e) => {
                          setBatchError(null);
                          setFieldValues((p) => ({
                            ...p,
                            [field]: e.target.value,
                          }));
                        }}
                        placeholder={`Enter ${label}`}
                        className={`w-full px-3 py-2 text-sm rounded-lg border ${isViolation ? "border-red-400 bg-red-50" : ""}`}
                      />
                      {isViolation && (
                        <p className="text-[10px] text-red-600 mt-0.5">
                          Must be {overallMin!.toFixed(2)}–{overallMax!.toFixed(2)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* VC error banner */}
        {batchError && (
          <div className="px-4 py-2 border-t border-red-200 bg-red-50 text-[11px] text-red-700 leading-relaxed flex items-start justify-between gap-2">
            <pre className="whitespace-pre-wrap font-sans">{batchError}</pre>
            <button
              type="button"
              onClick={() => setBatchError(null)}
              className="text-red-400 hover:text-red-600 shrink-0 text-base leading-none"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Footer */}
        <div
          className="px-6 py-4 flex justify-end items-center gap-4 mod-table-load-in"
          style={{ backgroundColor: "#FFFFFF", borderTop: "1px solid #E1E5E9" }}
        >
          <button
            type="button"
            onClick={resetForm}
            className="text-[#9b9aaa] hover:text-[#7d7c8b] text-sm font-medium transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApply}
            disabled={!hasAnyValue || loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border bg-[#F1935C] text-white hover:bg-[#e6844b] transition-all disabled:bg-[#E2E2E5] disabled:text-[#A9A8B1]"
          >
            {loading ? "Applying..." : "Apply"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BidCreationFilter;
